import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { LoadingSpinner } from '@/Components/common';
import socketService from '@/services/Common/SocketService';
import chatService, { ChatMessage } from '@/services/Common/ChatService';
import './ChatWithUs.css';

const ChatWithUs: React.FC = () => {
  const navigate = useNavigate();
  const [isNavigating, setIsNavigating] = useState(false);
  const [isConnecting, setIsConnecting] = useState(true);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [chatStatus, setChatStatus] = useState<'waiting' | 'active' | 'closed'>('waiting');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [agentTyping, setAgentTyping] = useState(false);
  const [agentEmail, setAgentEmail] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom when new messages arrive
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize chat on component mount
  useEffect(() => {
    const initializeChat = async () => {
      try {
        setIsConnecting(true);

        // Get auth token
        const token = localStorage.getItem('authToken');
        if (!token) {
          setError('Authentication required. Please login.');
          setIsConnecting(false);
          return;
        }

        // Connect to Socket.io
        await socketService.connect(token);

        // Request chat session
        const response = await chatService.requestChat();

        if (response.success && response.conversationId) {
          setConversationId(response.conversationId);
          setChatStatus(response.status as 'waiting' | 'active' | 'closed');

          // Load chat history if rejoining
          if (response.status === 'active') {
            const history = await chatService.getChatHistory(response.conversationId);
            setMessages(history);
          }
        }

        setIsConnecting(false);
      } catch (error: any) {
        console.error('Error initializing chat:', error);
        setError(error.message || 'Failed to initialize chat');
        setIsConnecting(false);
      }
    };

    initializeChat();

    // Set up event listeners
    chatService.onMessage(handleIncomingMessage);
    chatService.onTypingIndicator(handleTypingIndicator);
    chatService.onAgentJoined(handleAgentJoined);
    chatService.onChatClosed(handleChatClosed);

    // Cleanup on unmount
    return () => {
      chatService.offMessage();
      chatService.offTypingIndicator();
      chatService.offAgentJoined();
      chatService.offChatClosed();
      socketService.disconnect();
    };
  }, []);

  // Handle incoming messages
  const handleIncomingMessage = (message: ChatMessage) => {
    setMessages((prev) => [...prev, message]);

    // Mark as read if from agent
    if (message.senderType === 'agent' && conversationId) {
      chatService.markMessagesAsRead(conversationId, [message.messageId]);
    }
  };

  // Handle typing indicator
  const handleTypingIndicator = (data: {
    conversationId: string;
    user: { uuid: string; email: string };
    isTyping: boolean;
  }) => {
    if (data.conversationId === conversationId) {
      setAgentTyping(data.isTyping);
    }
  };

  // Handle agent joined
  const handleAgentJoined = (data: {
    conversationId: string;
    agent: { uuid: string; email: string };
  }) => {
    if (data.conversationId === conversationId) {
      setAgentEmail(data.agent.email);
      setChatStatus('active');
    }
  };

  // Handle chat closed
  const handleChatClosed = (data: { conversationId: string }) => {
    if (data.conversationId === conversationId) {
      setChatStatus('closed');
    }
  };

  // Send message
  const handleSendMessage = async () => {
    if (!inputValue.trim() || !conversationId || chatStatus === 'closed') return;

    try {
      await chatService.sendMessage(conversationId, inputValue.trim());
      setInputValue('');

      // Stop typing indicator
      if (isTyping) {
        setIsTyping(false);
        chatService.sendTypingIndicator(conversationId, false);
      }
    } catch (error: any) {
      console.error('Error sending message:', error);
      setError('Failed to send message');
    }
  };

  // Handle typing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);

    if (!conversationId) return;

    // Send typing indicator
    if (!isTyping) {
      setIsTyping(true);
      chatService.sendTypingIndicator(conversationId, true);
    }

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Stop typing after 2 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      chatService.sendTypingIndicator(conversationId, false);
    }, 2000);
  };

  // Handle file upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !conversationId) return;

    try {
      await chatService.uploadFile(conversationId, file);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error: any) {
      console.error('Error uploading file:', error);
      setError(error.message || 'Failed to upload file');
    }
  };

  // Handle key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Navigate back
  const handleBack = () => {
    setIsNavigating(true);
    navigate(-1);
  };

  // Format timestamp
  const formatTime = (timestamp: Date) => {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Loading state
  if (isNavigating || isConnecting) {
    return (
      <div className="chat-container">
        <LoadingSpinner fullScreen size="lg" />
      </div>
    );
  }

  // Error state
  if (error && !conversationId) {
    return (
      <div className="chat-container">
        <button className="chat-back-button" onClick={handleBack}>
          <span className="chat-back-arrow">←</span>
          <span className="chat-back-text">Back</span>
        </button>
        <div className="chat-error">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-container">
      <button className="chat-back-button" onClick={handleBack}>
        <span className="chat-back-arrow">←</span>
        <span className="chat-back-text">Back</span>
      </button>

      <div className="chat-chat-container">
        <div className="chat-header">
          <div className="chat-header-icon">
            <svg className="chat-icon" viewBox="0 0 24 24" fill="white">
              <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
            </svg>
          </div>
          <div className="chat-header-content">
            <h1 className="chat-title">
              {chatStatus === 'waiting'
                ? 'Waiting for agent...'
                : chatStatus === 'active'
                ? `Chat with ${agentEmail || 'Support Agent'}`
                : 'Chat Closed'}
            </h1>
            <div className="chat-status-container">
              {chatStatus === 'active' && (
                <>
                  <span className="chat-online-dot"></span>
                  <span className="chat-status">Online</span>
                </>
              )}
              {chatStatus === 'waiting' && (
                <span className="chat-status">Please wait, connecting you to an agent...</span>
              )}
            </div>
          </div>
        </div>

        <div className="chat-messages-container">
          {messages.map((message) => (
            <div
              key={message.messageId}
              className={`chat-message-wrapper ${
                message.senderType === 'user' ? 'user-message' : ''
              }`}
            >
              {message.senderType === 'agent' && (
                <div className="chat-bot-avatar">
                  <svg className="chat-icon" viewBox="0 0 24 24" fill="white">
                    <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
                  </svg>
                </div>
              )}
              <div className={`chat-message-bubble ${message.senderType === 'agent' ? 'bot' : 'user'}`}>
                {message.messageType === 'text' && (
                  <p className="chat-message-text">{message.content}</p>
                )}
                {(message.messageType === 'file' || message.messageType === 'image') && (
                  <div className="chat-file-message">
                    {message.messageType === 'image' && message.fileUrl && (
                      <img
                        src={`${process.env.REACT_APP_API_BASE_URL}${message.fileUrl}`}
                        alt={message.fileName}
                        className="chat-image"
                      />
                    )}
                    <a
                      href={`${process.env.REACT_APP_API_BASE_URL}${message.fileUrl}`}
                      download={message.fileName}
                      className="chat-file-link"
                    >
                      📎 {message.fileName}
                    </a>
                  </div>
                )}
                <span className="chat-message-time">{formatTime(message.timestamp)}</span>
              </div>
            </div>
          ))}

          {agentTyping && (
            <div className="chat-message-wrapper">
              <div className="chat-bot-avatar">
                <svg className="chat-icon" viewBox="0 0 24 24" fill="white">
                  <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
                </svg>
              </div>
              <div className="chat-message-bubble bot typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="chat-input-container">
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
            accept="image/*,.pdf,.doc,.docx,.txt"
          />
          <button
            className="chat-attach-button"
            onClick={() => fileInputRef.current?.click()}
            disabled={chatStatus !== 'active'}
            title="Attach file"
          >
            📎
          </button>
          <input
            type="text"
            placeholder={
              chatStatus === 'waiting'
                ? 'Waiting for agent...'
                : chatStatus === 'closed'
                ? 'Chat closed'
                : 'Type your message...'
            }
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            className="chat-input"
            disabled={chatStatus !== 'active'}
          />
          <button
            className="chat-send-button"
            onClick={handleSendMessage}
            disabled={chatStatus !== 'active' || !inputValue.trim()}
          >
            <svg className="chat-send-icon" viewBox="0 0 24 24" fill="white">
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatWithUs;
